/**
 * PropertyInfo — PF v2 native configuration UI (Wave 1).
 *
 * Lifts the presentational markup from the legacy `PropertyInformationStepController`
 * (`PurchaseFlow/steps/PropertyInformationStep`) but rewrites ALL data wiring onto
 * the PF v2 store via `usePropertyInfoConfig` — no constate provider, no imperative
 * handle, no legacy controller. F-B1 dies by construction: state lives in
 * `productConfigs` (placed there by a synchronous hydrate `set()` before this
 * mounts), and mounting only READS it. The legacy constate default-seeding
 * mount effect (`context.ts:244`) is NOT ported — "show one blank sheet" is a
 * read-boundary synth in the ops hook (spec §5).
 *
 * The DocEstate search-product upsell is BACK. It was dropped on the
 * premise that `searchProduct` is "always undefined in v2" — true, but the cause
 * was missing Salesforce data, not a retired feature: v2 only surfaces extras a
 * product is RELATED to, and nothing relates this one. The card
 * therefore renders only once the catalogue offers the extra, and is inert until
 * then.
 *
 * Dropped vs. the legacy controller (still dead in v2 — spec §4a):
 *   - the `isLandLordLandSheetInformationMandatory` gate (name/landlord never
 *     required; `required`/error UI dropped),
 *   - nothing on the analytics axis any more: the document configurator announces
 *     this page for `ENTER_ORDER_PROPERTY_INFO_PAGE_SCREEN`, and both
 *     `EDIT_LAND_REGISTER_SHEET_MANUALLY` and `PARCEL_SELECTED` are back as the
 *     two directions of one `land_register_source_chosen`. Legacy's parcel list
 *     is deliberately not sent — it is property data, not a funnel input.
 *
 * ZERO imports from `components/pages/PurchaseFlow/*` (DoD grep gate). i18n reuses
 * the legacy `purchase_flow:property_information_step.*` keys verbatim.
 */

import React, { useCallback, useEffect, useState } from 'react'
import { Collapse, Fade, Grid, Stack } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import moment, { type Moment } from 'moment-timezone'

import { BEIGE_600, GRAY_800 } from 'constants/styling/theme'
import ControlPointRoundedIcon from '@mui/icons-material/ControlPointRounded'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDatePicker } from 'components/common/MUIDatePicker'
import { MUIDivider } from 'components/common/MUIDivider'
import { MUIInputField } from 'components/common/MUIInputField'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { TransitionGroup } from 'react-transition-group'
import { useActionPopup } from 'utils/hooks'

import { AuthoritiesDocumentDataSource } from 'models/purchaseFlow/AuthoritiesDocumentDTO'
import { formatPrice } from 'utils/price/priceFormatter'
import { MUICheckCard } from 'components/common/MUICards/MUICheckCard'
import { Typography } from '@mui/material'
import { GRAY_700 } from 'constants/styling/theme'

import type { LandLordInformationDTO } from 'models/purchaseFlow/AuthoritiesDocumentDTO'
import type { CartItemId } from 'models/purchaseFlowRevamp'

import { selectProperty, usePfStore } from '../../store'
import type { CartMutationHandler } from '../../adapters/bridge'
import { usePropertyInfoConfig } from './usePropertyInfoConfig'
import { useLandRegistrationSearchExtra } from './useLandRegistrationSearchExtra'
import { PfRevampEventName } from '../../analytics/events'
import { dispatchPfRevampEvent } from '../../analytics/usePfRevampAnalytics'
import { ParcelViewerMap, ParcelViewerMapPopup } from 'components/common/ParcelViewerMap'
import { LandRegisterSheet } from './components/LandRegisterSheet.component'
import { FloatingScrollButton } from 'components/common/FloatingScrollButton'
import { useIsConfigPanelActive } from '../sourceVisuals/configPanelActive'

export interface PropertyInfoProps {
  cartItemId: CartItemId
  /** Attaches / detaches the land-registration-search extra. */
  onCartMutation?: CartMutationHandler
}

/**
 * @component PropertyInfo
 * Native property-information configuration form (DocEstate parcel map + nested
 * land-register CRUD + landlord + comment). Reads/writes `productConfigs` via
 * `usePropertyInfoConfig`.
 */
export const PropertyInfo: React.FC<PropertyInfoProps> = ({ cartItemId, onCartMutation }) => {
  const { t } = useTranslation(['purchase_flow'])
  const { showConfirm } = useActionPopup()

  const { value, patch, sheetOps } = usePropertyInfoConfig(cartItemId)
  const property = usePfStore(selectProperty)
  const { extra: searchExtra, isSelected: isSearching } = useLandRegistrationSearchExtra(cartItemId)

  const [showError, setShowError] = useState(false)
  const [isEditManuallyChosen, setIsEditManually] = useState(false)
  const [isParcelMapOpen, setIsParcelMapOpen] = useState(false)
  const isPanelActive = useIsConfigPanelActive()

  // A resumed draft keeps no page state, but a lookup billed with the sheets still sent is exactly what "Edit manually" saved.
  const isEditManually = isEditManuallyChosen || (isSearching && value.searchLandRegistrationDetails === false)

  const scrollParentId = `pf-chip-panel-${cartItemId}|property_info`
  const landlord = value.landLordInformation
  const dateOfBirth: Moment | null =
    landlord?.dateOfBirth ? moment(landlord.dateOfBirth, 'YYYY-MM-DD') : null

  const handleBlur = useCallback(() => setShowError(true), [])

  const updateLandlord = useCallback((partial: Partial<LandLordInformationDTO>) => {
    patch({
      landLordInformation: {
        firstName: landlord?.firstName ?? '',
        lastName: landlord?.lastName ?? '',
        dateOfBirth: landlord?.dateOfBirth ?? '',
        ...partial,
      },
    })
  }, [patch, landlord])

  const searchPrice = searchExtra?.prices[0]

  /** Attach or detach the upsell. Billing only — whether the client supplied the
   *  data is a separate question, answered by [setSearching]. */
  const setUpsellAttached = useCallback((attached: boolean) => {
    if (!searchExtra || !onCartMutation) return

    onCartMutation(attached
      ? { type: 'select-extra', productId: searchExtra.id, parentCartItemId: cartItemId, count: 1 }
      : { type: 'unselect-extra', productId: searchExtra.id, parentCartItemId: cartItemId })
  }, [searchExtra, onCartMutation, cartItemId])

  /**
   * Attach the upsell AND record that the client is leaving the sheets to us.
   *
   * The flag empties the sheets on the wire, so it must not be set for a client
   * who typed them: the lookup is billed either way, but hand-typed sheets are
   * what DocEstate starts from.
   */
  const setSearching = useCallback((searching: boolean) => {
    setUpsellAttached(searching)
    patch({ searchLandRegistrationDetails: searching })
  }, [setUpsellAttached, patch])

  // "Edit manually" — legacy quotes the upsell's real price here and then SELECTS
  // it (`controller.tsx:114`): hand-typed sheets are unverified, so BKBN does the
  // lookup either way. `not_applicable` is the honest fallback while no extra is
  // offered, matching the already-degraded shipped legacy text.
  const onEditManually = useCallback(async () => {
    const confirmed = await showConfirm(
      <Trans
        t={t}
        i18nKey="property_information_step.land_register_sheet.edit_manually_description"
        values={{
          price: searchPrice
            ? formatPrice(searchPrice.unitPrice, searchPrice.currency)
            : t('not_applicable'),
        }}
      />,
      { title: t('property_information_step.land_register_sheet.edit_manually_title') }
    )
    if (!confirmed) return

    setIsEditManually(true)
    // Manual entry is not map-sourced.
    patch({ dataSource: undefined })
    // The lookup is billed (legacy `controller.tsx:114` selects it here too), but
    // the client is supplying the sheets, so they must reach DocEstate.
    setUpsellAttached(true)
    patch({ searchLandRegistrationDetails: false })

    // Only after the confirm is accepted: the client has actually refused the
    // lookup, which is the decision worth counting.
    dispatchPfRevampEvent({
      type: PfRevampEventName.LAND_REGISTER_SOURCE_CHOSEN,
      payload: { cartItemId, source: 'manual' },
    })
  }, [showConfirm, t, searchPrice, patch, setUpsellAttached, cartItemId])

  // Show the DocEstate help popup once per browser (localStorage-only; NOT a
  // persisted-store write — read-only-mount stays intact). The popup is a portal,
  // so a hidden tab must not open it over the one the client is looking at.
  useEffect(() => {
    if (!isPanelActive) return
    if (!localStorage.getItem('hasSeenParcelViewerMapPopup')) {
      setIsParcelMapOpen(true)
      localStorage.setItem('hasSeenParcelViewerMapPopup', 'true')
    }
  }, [isPanelActive])

  return (
    <Fade in>
      <Stack gap={2}>

        {/* "BKBN FINDS YOUR PROPERTY DATA" UPSELL — renders only once the
            catalogue relates the extra (ISSUE-3329). */}
        {searchExtra &&
          <MUICheckCard
            disabledSelection={isEditManually}
            highlighted={isSearching}
            checked={isSearching}
            onCheck={() => {
              // While editing manually the extra is mandatory — legacy locked the
              // card for exactly this reason.
              if (isEditManually) return
              setSearching(!isSearching)
            }}
          >
            <Stack gap={0.4} padding="1.6rem">

              <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>

                <Typography variant="text-md" fontWeight={500}>
                  {searchExtra.displayName}
                </Typography>

                {searchPrice &&
                  <Typography variant="text-md" fontWeight={500} whiteSpace="nowrap">
                    {formatPrice(searchPrice.unitPrice, searchPrice.currency)}
                  </Typography>
                }

              </Stack>

              {searchExtra.description &&
                <Typography variant="text-sm" color={GRAY_700}>
                  {searchExtra.description}
                </Typography>
              }

            </Stack>
          </MUICheckCard>
        }

        {/* LAND INFO — hidden while BKBN is doing the lookup, unless the client
            asked to type it in anyway (legacy `Collapse in={…}`). */}
        <Collapse in={!isSearching || isEditManually}>

          <ParcelViewerMap
            selectedPropertyLocation={property?.coordinates}
            onParcelChange={(grouped) => {
              sheetOps.setSheetsFromMap(grouped)
              setIsEditManually(false)
              dispatchPfRevampEvent({
                type: PfRevampEventName.LAND_REGISTER_SOURCE_CHOSEN,
                payload: { cartItemId, source: 'map', districtCount: Object.keys(grouped).length },
              })
              // Picking parcels IS finding the data — legacy dropped the upsell here.
              patch({ dataSource: AuthoritiesDocumentDataSource.MAP })
              setSearching(false)
            }}
            onOpenParcelViewerPopup={() => setIsParcelMapOpen(true)}
          />

          <ParcelViewerMapPopup isOpen={isParcelMapOpen} onClose={() => setIsParcelMapOpen(false)} />

          {/* LAND REGISTER SHEETS */}
          <Stack gap={2} paddingBottom="1.6rem">
            <TransitionGroup component={null}>
              {value.landRegistrationSheets.map((sheet, index) => (
                <Collapse key={index}>
                  <LandRegisterSheet
                    sheet={sheet}
                    sheetIndex={index}
                    isFirst={index === 0}
                    isEditManually={isEditManually}
                    showError={showError}
                    sheetOps={sheetOps}
                    onBlur={handleBlur}
                    onEditManually={onEditManually}
                  />
                </Collapse>
              ))}
            </TransitionGroup>
          </Stack>

          {isEditManually &&
            <MUIDivider margin={8} sx={{ borderColor: BEIGE_600, borderTopColor: BEIGE_600 }}>
              <MUIButton
                type="secondaryBorder"
                onClick={() => sheetOps.addSheet()}
                startIcon={<ControlPointRoundedIcon fontSize="large" sx={{ color: GRAY_800 }} />}
              >
                {t('property_information_step.land_register_sheet.add_land_register_sheet')}
              </MUIButton>
            </MUIDivider>
          }

        </Collapse>

        {/* LANDLORD INFORMATION */}
        <SectionedBorderBox title={t('property_information_step.landlord_info.title')}>
          <Stack spacing={2}>

            <Grid container gap={2}>

              <Grid item xs={4}>
                <MUIInputField
                  value={landlord?.firstName ?? ''}
                  label={t('property_information_step.landlord_info.input_landlord_first_name')}
                  onBlur={handleBlur}
                  onChange={(e) => updateLandlord({ firstName: e.target.value })}
                />
              </Grid>

              <Grid item xs={4}>
                <MUIInputField
                  value={landlord?.lastName ?? ''}
                  label={t('property_information_step.landlord_info.input_landlord_last_name')}
                  onBlur={handleBlur}
                  onChange={(e) => updateLandlord({ lastName: e.target.value })}
                />
              </Grid>

            </Grid>

            <Stack width="33%" gap={0.6}>
              <MUIDatePicker
                value={dateOfBirth}
                label={t('property_information_step.landlord_info.input_date_birth')}
                views={['year', 'month', 'day']}
                format="DD.MM.YYYY"
                onChange={(date) => updateLandlord({
                  dateOfBirth: date && date.isValid() ? date.format('YYYY-MM-DD') : '',
                })}
              />
            </Stack>

          </Stack>
        </SectionedBorderBox>

        {/* COMMENT */}
        <SectionedBorderBox title={t('property_information_step.comment.title')}>
          <MUIInputField
            placeholder={t('property_information_step.comment.placeholder')}
            isMultiline
            value={value.comment ?? ''}
            onChange={(e) => patch({ comment: e.target.value })}
          />
        </SectionedBorderBox>

        <FloatingScrollButton parentId={scrollParentId} />

      </Stack>
    </Fade>
  )
}
